import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { ButtonWithTooltip } from "../ButtonWithTooltip";
import { messages } from "../../../locales/messages";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { useLoading } from "../../../contextApi/LoadingContext";

export const UrlImagePasteUpload: React.FC<{
  handleOnImageFileSelect: (
    e: React.ChangeEvent<HTMLInputElement>,
    path?: string,
  ) => Promise<void> | void;
  busy?: boolean;
  compact?: boolean;
  /**
   * Z-index for the dialog
   * @default 1300
   */
  zIndex?: number;
}> = ({ handleOnImageFileSelect, busy, compact = false, zIndex = 1300 }) => {
  const { t } = useTranslation();
  const { showLoadingScreen } = useLoading();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [previewSrc, setPreviewSrc] = useState<string>("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [inputHint, setInputHint] = useState<string>("");
  const [imgError, setImgError] = useState<string>("");

  // Align allowed formats with useUploadOrPickImages and file input accept="image/*,.psd"
  // - Allow common image extensions and psd by URL
  // - On fetch confirm, also accept by MIME (image/* or image/vnd.adobe.photoshop)
  const ALLOWED_URL_EXT_RE =
    /\.(png|jpe?g|gif|webp|bmp|svg|heic|tiff?|avif|psd)$/i;
  const MAX_SIZE_MB = 10;

  const isAllowedUrlExt = (u: string) => {
    try {
      const urlObj = new URL(u);
      const clean = decodeURIComponent(urlObj.pathname);
      return ALLOWED_URL_EXT_RE.test(clean);
    } catch {
      return ALLOWED_URL_EXT_RE.test(u);
    }
  };

  const isAllowedBlobOrName = (blob: Blob, fileName: string) => {
    const type = (blob.type || "").toLowerCase();
    if (type.startsWith("image/")) return true;
    if (type === "image/vnd.adobe.photoshop") return true;
    return /\.psd$/i.test(fileName) || ALLOWED_URL_EXT_RE.test(fileName);
  };

  const resetState = () => {
    setUrl("");
    setPreviewSrc("");
    setLoadingPreview(false);
    setUploading(false);
  };

  const openDialog = () => {
    if (busy) return;
    setOpen(true);
  };
  const closeDialog = () => {
    setOpen(false);
    resetState();
  };

  const deriveFileName = (u: string) => {
    try {
      const urlObj = new URL(u);
      const extPattern = /\.(png|jpe?g|gif|webp|bmp|svg|heic|tiff?|avif|psd)$/i;

      // 1) Try last path segment
      let lastSeg = urlObj.pathname.split("/").filter(Boolean).pop() || "";
      lastSeg = decodeURIComponent(lastSeg);
      if (extPattern.test(lastSeg)) return lastSeg;

      // 2) Try common query params
      const qp = urlObj.searchParams;
      const queryKeys = ["filename", "file", "name", "download", "key", "Key"];
      for (const k of queryKeys) {
        const v = qp.get(k);
        if (!v) continue;
        const decoded = decodeURIComponent(v);
        if (extPattern.test(decoded)) return decoded.split("/").pop() as string;
      }

      // 3) Try response-content-disposition param
      const rcd = qp.get("response-content-disposition");
      if (rcd) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
          rcd,
        );
        if (match) {
          const fn = decodeURIComponent(match[1] || match[2]);
          if (extPattern.test(fn)) return fn.split("/").pop() as string;
        }
      }

      // 4) Fallback: look anywhere in URL for a filename with supported extension
      const whole = decodeURIComponent(u);
      const m = whole.match(
        /([^/?#]+\.(?:png|jpe?g|gif|webp|bmp|svg|heic|tiff?|avif|psd))(?:[?#]|$)/i,
      );
      if (m) return m[1];

      // 5) Last resort
      return lastSeg || "pasted-image.png";
    } catch {
      return "pasted-image.png";
    }
  };

  const handleLoadPreview = async () => {
    if (!url) return;
    try {
      const u = new URL(url);
      if (!(u.protocol === "http:" || u.protocol === "https:")) {
        setInputHint(t(messages.Enter_a_valid_image_URL_http_https()));
        return;
      }
    } catch {
      setInputHint("Enter a valid image URL (http/https)");
      return;
    }

    if (!isAllowedUrlExt(url)) {
      setInputHint(
        t(
          messages.Only_image_URLs_with_extensions_png_jpg_jpeg_gif_webp_bmp_svg_heic_tif_tiff_avif_or_psd_are_allowed(),
        ),
      );
      return;
    }

    setInputHint("");
    setImgError("");
    setLoadingPreview(true);
    setPreviewSrc(url);
  };

  const handleResetPreview = () => {
    setLoadingPreview(false);
    setImgError("");
    setPreviewSrc("");
  };

  const handleConfirmUpload = async () => {
    if (!url) return;
    showLoadingScreen(true);
    setUploading(true);
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const derivedName = deriveFileName(url);

      if (!isAllowedBlobOrName(blob, derivedName)) {
        throw new Error(
          t(messages.Unsupported_file_type_Allowed_image_or_psd()),
        );
      }
      if (blob.size > MAX_SIZE_MB * 1024 * 1024) {
        throw new Error(
          t(
            messages.Image_size_exceeds_the_10MB_limit_Please_use_a_smaller_image(),
          ),
        );
      }

      const file = new File([blob], derivedName, {
        type: blob.type || "image/png",
      });

      const dt = new DataTransfer();
      dt.items.add(file);
      const fakeEvent = {
        target: { files: dt.files },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      await Promise.resolve(
        handleOnImageFileSelect(fakeEvent, "productImages"),
      );
      toastSuccess(t(messages.uploadImages()));
      setOpen(false);
      resetState();
    } catch (err) {
      const msg = (err as Error)?.message?.includes("Unsupported file type")
        ? t(messages.Unsupported_file_type_Allowed_image_or_psd())
        : (err as Error)?.message?.includes("exceeds")
          ? t(
              messages.Image_size_exceeds_the_10MB_limit_Please_use_a_smaller_image(),
            )
          : undefined;
      toastError(
        msg ||
          t(messages.failedToRemoveBackground?.()) ||
          t(
            messages.Unable_to_fetch_image_from_URL_Try_downloading_and_uploading_locally(),
          ),
      );
    } finally {
      setUploading(false);
      showLoadingScreen(false);
    }
  };

  return (
    <>
      <ButtonWithTooltip
        onClick={openDialog}
        disabled={!!busy}
        size={compact ? "small" : "medium"}
        customStyles={
          compact
            ? {
                marginTop: 4,
                backgroundColor: "#ff8038",
                color: "#fff",
                textTransform: "capitalize",
                fontWeight: 500,
                width: "100%",
                minHeight: "33px",
                padding: "2px 8px",
                fontSize: "14px",
                lineHeight: 1.2,
              }
            : {
                marginTop: 8,
                backgroundColor: "#ff8038",
                color: "#fff",
                textTransform: "capitalize",
                fontWeight: 600,
                width: "100%",
              }
        }
        data-testid="paste-image-url-button"
        tooltipText={t(messages.Paste_a_direct_image_link_to_upload())}
        tooltipPlacement="top"
      >
        {t(messages.Paste_image_URL())}
      </ButtonWithTooltip>

      <Dialog
        open={open}
        onClose={closeDialog}
        aria-labelledby="paste-image-url-title"
        maxWidth={false}
        sx={{ zIndex }}
        PaperProps={{
          sx: {
            m: { xs: 1.5, sm: 4 },
            width: { xs: "calc(100% - 24px)", sm: 960, md: 1200, lg: 1400 },
            maxWidth: "90vw",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle id="paste-image-url-title">
          {t(messages.Paste_image_URL())}
        </DialogTitle>
        <DialogContent dividers>
          {!previewSrc ? (
            <>
              <TextField
                autoFocus
                fullWidth
                label={t(messages.Image_URL())}
                placeholder="https://example.com/image.png"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!previewSrc) handleLoadPreview();
                    else handleConfirmUpload();
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    caretColor: "#ff8038",
                    "& fieldset": {
                      borderColor: "#ff8038",
                    },
                    "&:hover fieldset": {
                      borderColor: "#ff8038",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#ff8038",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#ff8038",
                    "&.Mui-focused": {
                      color: "#ff8038",
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#ff8038",
                    opacity: 0.6,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={handleLoadPreview}
                        disabled={!url || loadingPreview}
                        sx={{ color: "#ff8038" }}
                      >
                        {loadingPreview
                          ? t(messages.Loading())
                          : t(messages.Preview())}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              {inputHint && (
                <Typography
                  variant="caption"
                  sx={{ color: "#ff8038", mt: 0.5, display: "block" }}
                >
                  {inputHint}
                </Typography>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" noWrap>
                {t(messages.Loaded())}: {deriveFileName(url)}
              </Typography>
              <Button
                size="small"
                onClick={handleResetPreview}
                sx={{ color: "#ff8038" }}
              >
                {t(messages.Change_URL())}
              </Button>
            </Box>
          )}

          <Box
            mt={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #e5e7eb",
              borderRadius: 1,
              p: 1,
              minHeight: 180,
              backgroundColor: "#fafafa",
            }}
          >
            {previewSrc ? (
              <img
                src={previewSrc}
                alt={deriveFileName(url)}
                onLoad={() => setLoadingPreview(false)}
                onError={() => {
                  setLoadingPreview(false);
                  setImgError(t(messages.Could_not_load_image_Check_the_URL()));
                  setPreviewSrc("");
                }}
                style={{
                  maxWidth: "100%",
                  maxHeight: 420,
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            ) : loadingPreview ? (
              <Typography variant="body2" sx={{ color: "#ff8038" }}>
                {t(messages.Loading_preview())}
              </Typography>
            ) : imgError ? (
              <Typography variant="body2" sx={{ color: "#ff8038" }}>
                {imgError}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(messages.Paste_a_direct_image_link_and_click_Preview())}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="inherit">
            {t(messages.Cancel?.()) || "Cancel"}
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            disabled={!previewSrc || uploading}
            sx={{
              backgroundColor: "#ff8038",
              ":hover": { backgroundColor: "#e67330" },
            }}
          >
            {uploading
              ? t(messages.processing?.()) || "Uploading"
              : t(messages.Upload?.()) || "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UrlImagePasteUpload;
